// Single-type variant of MobileSearchOverlay. Used by DJPalSearchBar on
// mobile so per-tab search bars (Set Planner, Mixpedia per-tab) get the
// same full-screen SoundCloud-style experience as the navbar.
//
// Matches DJPalSearchBar's prop shape so wiring it up is a near drop-in.
// The parent owns the query value and dispatches the actual search via
// onSuggestionSelect / onRecentSelect — same as the inline-dropdown flow.
//
// Body uses page-level scroll (no inner overflow container), so the
// flick-to-scroll → click-on-tap iOS bug doesn't apply.
//
// Also drives the in-place list filters on phones (MobileListSearch): with
// `minChars={0}` + a synchronous `fetchSuggestions` it becomes a full-screen
// filter of a local list — the whole list shows on open, narrows as you
// type, rows do the row's action, and Enter (`onSubmit`) hands the query
// back to the underlying list instead.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import useOverlayDismiss from './common/useOverlayDismiss';
import ReactDOM from 'react-dom';
import { MIN_SEARCH_CHARS } from '../utils/searchConfig';
import { entityId } from '../utils/entityId';
import CoverArt from './CoverArt';
import './MobileSearchOverlay.scss';

const MobileTypedSearchOverlay = ({
    open,
    onClose,
    placeholder = 'Search',
    fetchSuggestions,
    onSuggestionSelect,
    recentEntries = [],
    recentLabel = 'Recently searched',
    onRecentSelect,
    onRecentDismiss,
    onRecentClear,
    // Local-filter mode knobs (see header comment). Defaults = the classic
    // remote-autocomplete behaviour.
    minChars = MIN_SEARCH_CHARS,
    debounceMs = 250,
    initialQuery = '',
    onSubmit,
}) => {
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const { closing, fading, requestClose } = useOverlayDismiss(open, onClose, inputRef);
    const debounceRef = useRef(null);
    const fetchSeqRef = useRef(0);

    const runFetch = useCallback(async (q) => {
        if (!fetchSuggestions || q.length < minChars) {
            setSuggestions([]);
            return;
        }
        const seq = ++fetchSeqRef.current;
        setLoading(true);
        try {
            const result = await fetchSuggestions(q);
            if (seq !== fetchSeqRef.current) return;
            setSuggestions(Array.isArray(result) ? result : []);
        } catch {
            if (seq === fetchSeqRef.current) setSuggestions([]);
        } finally {
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    }, [fetchSuggestions, minChars]);

    // Reset state on each open. Defer focus so the keyboard pops up after
    // the overlay's mount animation settles.
    useEffect(() => {
        if (!open) return undefined;
        setQuery(initialQuery || '');
        setSuggestions([]);
        setLoading(false);
        // Local-filter mode: show the (whole) list straight away.
        if ((initialQuery || '').trim().length >= minChars) {
            runFetch((initialQuery || '').trim());
        }
        const focusTimer = setTimeout(() => inputRef.current?.focus(), 50);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            clearTimeout(focusTimer);
            document.body.style.overflow = prevOverflow;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);


    const handleChange = (val) => {
        setQuery(val);
        clearTimeout(debounceRef.current);
        if (val.trim().length < minChars) {
            // Invalidate any slow in-flight fetch from a longer prior query so
            // it can't repopulate stale suggestions after the user deletes.
            fetchSeqRef.current++;
            setSuggestions([]);
            setLoading(false);
            return;
        }
        if (debounceMs <= 0) runFetch(val.trim());
        else debounceRef.current = setTimeout(() => runFetch(val.trim()), debounceMs);
    };

    if (!open) return null;

    const showRecents = query.trim().length < minChars && recentEntries.length > 0;
    const handleSelect = (action) => () => { action(); requestClose(); };

    return ReactDOM.createPortal(
        <div className={`mobile-search-overlay${closing ? ' is-closing' : ''}${fading ? ' is-fading' : ''}`} role="dialog" aria-modal="true" aria-label="Search">
            <div className="mso-top">
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        // Per-tab search bars commit via suggestion select — bare Enter
                        // does nothing. Local-filter mode hands the query back to the
                        // list (onSubmit) and closes.
                        if (onSubmit) { onSubmit(query.trim()); requestClose(); }
                    }}
                    className="mso-form"
                >
                    <div className="mso-input-pill">
                        <svg className="mso-input-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
                        </svg>
                        <input
                            ref={inputRef}
                            type="search"
                            inputMode="search"
                            enterKeyHint="search"
                            autoComplete="off"
                            autoCorrect="off"
                            autoCapitalize="off"
                            spellCheck={false}
                            value={query}
                            onChange={(e) => handleChange(e.target.value)}
                            placeholder={placeholder}
                            className="mso-input"
                        />
                        {loading && <div className="mso-spinner" />}
                        {!loading && query && (
                            <button
                                type="button"
                                className="mso-input-clear"
                                onClick={() => handleChange('')}
                                aria-label="Clear search"
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="18" y1="6" x2="6" y2="18" />
                                    <line x1="6" y1="6" x2="18" y2="18" />
                                </svg>
                            </button>
                        )}
                    </div>
                </form>
                <button type="button" className="mso-cancel" onClick={requestClose}>Cancel</button>
            </div>

            <div className="mso-body">
                {showRecents && (
                    <div className="mso-section">
                        <div className="mso-section-header">{recentLabel}</div>
                        {recentEntries.map((entry) => {
                            const primary = entry.entityName || '';
                            const secondary = entry.entitySubtitle || null;
                            const fallback = (primary || '?').charAt(0).toUpperCase();
                            const circle = entry.entityType === 'artist';
                            return (
                                <button
                                    key={entry.id}
                                    type="button"
                                    className="mso-row mso-history-row"
                                    onClick={handleSelect(() => onRecentSelect && onRecentSelect(entry))}
                                >
                                    {entry.entityArtwork
                                        ? <CoverArt src={entry.entityArtwork} size={48} eager className="mso-artwork" style={{ borderRadius: circle ? '50%' : 6 }} />
                                        : <div className="mso-artwork mso-artwork-placeholder" style={{ borderRadius: circle ? '50%' : 6 }}>{fallback}</div>
                                    }
                                    <div className="mso-row-text">
                                        <span className="mso-row-primary">{primary}</span>
                                        {secondary && <span className="mso-row-secondary">{secondary}</span>}
                                    </div>
                                    {onRecentDismiss && (
                                        <button
                                            type="button"
                                            className="mso-row-dismiss"
                                            onClick={(e) => { e.stopPropagation(); onRecentDismiss(entry.id); }}
                                            aria-label="Remove from recent searches"
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                                <line x1="18" y1="6" x2="6" y2="18" />
                                                <line x1="6" y1="6" x2="18" y2="18" />
                                            </svg>
                                        </button>
                                    )}
                                </button>
                            );
                        })}
                        {onRecentClear && (
                            <button type="button" className="mso-clear-all" onClick={onRecentClear}>
                                Clear recent searches
                            </button>
                        )}
                    </div>
                )}

                {!showRecents && suggestions.length > 0 && (
                    <div className="mso-section">
                        {suggestions.map((s, i) => {
                            const primary = s.title || s.name || '';
                            const secondary = s.subtitle || null;
                            const fallback = (primary || '?').charAt(0).toUpperCase();
                            return (
                                <button
                                    key={entityId(s) || `${primary}-${i}`}
                                    type="button"
                                    className="mso-row"
                                    onClick={handleSelect(() => onSuggestionSelect && onSuggestionSelect(s))}
                                >
                                    {s.artworkUrl
                                        ? (s.artworkWrapClassName
                                            ? <div className={`mso-artwork ${s.artworkWrapClassName}`} style={{ borderRadius: s.round ? '50%' : 6 }}><CoverArt src={s.artworkUrl} size={48} eager /></div>
                                            : <CoverArt src={s.artworkUrl} size={48} eager className="mso-artwork" style={{ borderRadius: s.round ? '50%' : 6 }} />)
                                        : <div className="mso-artwork mso-artwork-placeholder" style={{ borderRadius: s.round ? '50%' : 6 }}>{fallback}</div>
                                    }
                                    <div className="mso-row-text">
                                        <span className="mso-row-primary">{primary}</span>
                                        {secondary && <span className="mso-row-secondary">{secondary}</span>}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}

                {!showRecents && suggestions.length === 0 && query.trim().length >= Math.max(1, minChars) && !loading && (
                    <div className="mso-empty">No results for &ldquo;{query}&rdquo;</div>
                )}
            </div>
        </div>,
        document.body,
    );
};

export default MobileTypedSearchOverlay;
